"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { FaArrowRight, FaCalendarDays, FaCheck } from "react-icons/fa6";
import { FormattedMessage, useIntl } from "react-intl";
import { trackCtaClick, trackEvent } from "@/app/providers/ObservabilityProvider";
import { DEMO_URL } from "@/lib/demo";

interface ContactForm {
  subject: string;
  content: string;
  name: string;
  email: string;
}

export default function ContactSection() {
  const intl = useIntl();
  const [form, setForm] = useState<ContactForm>({
    subject: "",
    content: "",
    name: "",
    email: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof ContactForm, string>>>({});
  const [sendFailed, setSendFailed] = useState(false);
  const successRef = useRef<HTMLDivElement>(null);

  // Move focus to the confirmation so screen readers announce it and keyboard users are
  // not left on a submit button that no longer exists.
  useEffect(() => {
    if (sent) successRef.current?.focus();
  }, [sent]);

  const subjectOptions: { value: string; label: string }[] = [
    { value: "", label: intl.formatMessage({ id: "contact.selectSubject" }) },
    { value: "abonnements", label: intl.formatMessage({ id: "contact.subjectSubscriptions" }) },
    { value: "bug", label: intl.formatMessage({ id: "contact.subjectBug" }) },
    { value: "retour-utilisation", label: intl.formatMessage({ id: "contact.subjectFeedback" }) },
    { value: "collaboration", label: intl.formatMessage({ id: "contact.subjectCollaboration" }) },
    { value: "idee-feature", label: intl.formatMessage({ id: "contact.subjectFeatureIdea" }) },
    { value: "autre", label: intl.formatMessage({ id: "contact.subjectOther" }) },
  ];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    // A corrected field drops its error right away; the others wait for the next submit.
    if (errors[name as keyof ContactForm]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  /**
   * Checks the four required fields (the API rejects any empty one) and returns the
   * message per invalid field, in form order.
   */
  const validate = (): Partial<Record<keyof ContactForm, string>> => {
    const next: Partial<Record<keyof ContactForm, string>> = {};
    if (!form.name.trim()) next.name = intl.formatMessage({ id: "contact.error.name" });
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) next.email = intl.formatMessage({ id: "contact.error.email" });
    if (!form.subject) next.subject = intl.formatMessage({ id: "contact.error.subject" });
    if (!form.content.trim()) next.content = intl.formatMessage({ id: "contact.error.content" });
    return next;
  };

  /**
   * Validates, then posts the message. Invalid fields get an inline message and the first
   * one takes focus (WCAG 3.3.1); a failed send says so instead of failing silently.
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSendFailed(false);
    const found = validate();
    setErrors(found);
    const firstInvalid = (Object.keys(found) as (keyof ContactForm)[])[0];
    if (firstInvalid) {
      document.getElementById(`contact-${firstInvalid}`)?.focus();
      return;
    }
    trackEvent("contact-submit-button", { subject: form.subject });
    try {
      setIsLoading(true);
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (response.ok) {
        setForm({ name: "", email: "", subject: "", content: "" });
        setSent(true);
      } else {
        const data = await response.json().catch(() => ({}));
        console.error("Contact send error:", data.message);
        setSendFailed(true);
      }
    } catch (error) {
      console.error("Contact send error:", error);
      setSendFailed(true);
    } finally {
      setIsLoading(false);
    }
  };

  /** Props wiring a field to its error message. */
  const fieldA11y = (field: keyof ContactForm) =>
    errors[field]
      ? { "aria-invalid": true as const, "aria-describedby": `contact-${field}-error` }
      : {};

  /** The inline error under a field, if any. */
  const fieldError = (field: keyof ContactForm) =>
    errors[field] ? (
      <p id={`contact-${field}-error`} className="mt-1.5 text-sm font-medium text-error-main">
        {errors[field]}
      </p>
    ) : null;

  const inputClass =
    // 16 px on phones so iOS Safari doesn't zoom on focus; the focus ring is a ring, not a
    // thicker border, so the field's text never shifts. The resting border is the grey that
    // still reads 3:1 on white (WCAG 1.4.11): the pale one left the fields invisible.
    "w-full bg-surface border border-gray-main/80 rounded-xl px-4 py-3 text-base sm:text-sm text-text-main placeholder:text-gray-main focus:outline-none focus:border-blue-main focus:ring-2 focus:ring-blue-main/40 aria-[invalid=true]:border-error-main transition duration-150";
  const labelClass = "mb-1.5 block text-sm font-semibold text-text-main";

  return (
    <section id="contact" aria-labelledby="contact-title" className="w-full py-14 px-4 bg-gray-ulight sm:py-20 2xl:py-28">
      <div className="max-w-4xl mx-auto 2xl:max-w-5xl">

        {/* Header */}
        <div className="text-center mb-10">
          <h2 id="contact-title" className="text-3xl font-bold text-text-main mb-2 2xl:text-4xl">
            <FormattedMessage id="contact.title" />
          </h2>
          <p className="text-brand-ink font-semibold text-lg">
            <FormattedMessage id="contact.subtitle" />
          </p>
        </div>

        {/* Body */}
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-6">

          {/* Left: intro + demo card */}
          <div className="sm:col-span-2 flex flex-col gap-4">
            <div className="bg-blue-main rounded-xl px-6 py-6 flex flex-col gap-3">
              <h3 className="text-lg font-bold text-white leading-snug">
                <FormattedMessage id="contact.intro.title" />
              </h3>
              <p className="text-sm text-white leading-relaxed">
                <FormattedMessage id="contact.intro.desc" />
              </p>
            </div>

            <div className="bg-surface rounded-xl px-6 py-6 flex flex-col gap-4 border border-gray-light">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-blue-main flex items-center justify-center shrink-0">
                  <FaCalendarDays aria-hidden="true" className="text-blue-ulight text-base" />
                </div>
                <h3 className="font-bold text-text-main text-base">
                  <FormattedMessage id="contact.demo.title" />
                </h3>
              </div>
              <p className="text-sm text-gray-main leading-relaxed">
                <FormattedMessage id="contact.demo.desc" />
              </p>
              <Link
                href={DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackEvent("contact-demo-button", {});
                  trackCtaClick("contact", "demo");
                }}
                className="flex min-h-11 items-center justify-center gap-2 bg-blue-ulight text-brand-ink font-semibold text-sm py-2.5 rounded-full hover:bg-blue-light hover:scale-105 transition-all duration-200"
              >
                <FormattedMessage id="contact.demo.cta" />
                <FaArrowRight aria-hidden="true" className="text-xs" />
              </Link>
            </div>
          </div>

          {/* Right: form */}
          <div className="sm:col-span-3 bg-surface rounded-xl px-6 py-6 border border-gray-light">
            {sent ? (
              <div
                ref={successRef}
                tabIndex={-1}
                role="status"
                className="h-full flex flex-col items-center justify-center gap-4 text-center py-10 outline-none"
              >
                <div className="w-12 h-12 rounded-full bg-blue-main flex items-center justify-center" aria-hidden="true">
                  <FaCheck className="text-white text-lg" />
                </div>
                <p className="text-text-main font-semibold text-base">
                  <FormattedMessage id="contact.success" />
                </p>
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  className="min-h-11 rounded-full px-4 text-brand text-sm font-semibold hover:underline"
                >
                  <FormattedMessage id="contact.sendAnother" />
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
                <p className="text-xs text-gray-main">
                  <FormattedMessage id="contact.allRequired" />
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className={labelClass}>
                      {intl.formatMessage({ id: "contact.name" })}
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      autoComplete="name"
                      value={form.name}
                      onChange={handleChange}
                      className={inputClass}
                      required
                      {...fieldA11y("name")}
                    />
                    {fieldError("name")}
                  </div>
                  <div>
                    <label htmlFor="contact-email" className={labelClass}>
                      {intl.formatMessage({ id: "contact.email" })}
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      autoComplete="email"
                      inputMode="email"
                      value={form.email}
                      onChange={handleChange}
                      className={inputClass}
                      required
                      {...fieldA11y("email")}
                    />
                    {fieldError("email")}
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-subject" className={labelClass}>
                    {intl.formatMessage({ id: "contact.label.subject" })}
                  </label>
                  <select
                    id="contact-subject"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    className={`${inputClass} cursor-pointer`}
                    required
                    {...fieldA11y("subject")}
                  >
                    {subjectOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {fieldError("subject")}
                </div>

                <div>
                  <label htmlFor="contact-content" className={labelClass}>
                    {intl.formatMessage({ id: "contact.label.message" })}
                  </label>
                  <textarea
                    id="contact-content"
                    name="content"
                    value={form.content}
                    onChange={handleChange}
                    placeholder={intl.formatMessage({ id: "contact.placeholder" })}
                    className={`${inputClass} min-h-32 max-h-64 resize-y`}
                    required
                    {...fieldA11y("content")}
                  />
                  {fieldError("content")}
                </div>

                {sendFailed && (
                  <p role="alert" className="rounded-xl bg-error-ulight px-4 py-3 text-sm font-medium text-error-main">
                    <FormattedMessage id="contact.error.send" />
                  </p>
                )}

                <div className="flex justify-end mt-1">
                  {/* Never disabled: an unexplained greyed-out button tells no one what is
                      missing. A submit with gaps lists them under each field instead. */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    aria-busy={isLoading}
                    className="flex min-h-11 items-center gap-2 bg-blue-main text-white font-semibold text-sm px-7 py-3 rounded-full hover:bg-blue-fill hover:scale-105 transition-all duration-200 disabled:cursor-wait disabled:scale-100"
                  >
                    {isLoading ? (
                      <>
                        <span aria-hidden="true" className="inline-block h-4 w-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
                        <FormattedMessage id="contact.sending" />
                      </>
                    ) : (
                      <>
                        <FormattedMessage id="contact.submit" />
                        <FaArrowRight aria-hidden="true" className="text-xs" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
